import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import UserForm, { UserFormData, userFormInitialValues } from 'components/forms/user/UserForm';
import { USER_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mapUserFormToCreatePayload } from 'pages/admin-module/user/user-page-mappers';
import { EDIT_USER_PAGE_PATH, USERS_PAGE_PATH } from 'routes/paths';
import { DUPLICATE_USER_ERROR_CODE, ERROR_NOTIFICATION_COLOR, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to add a new user.
 *
 */
export default function AddUserPage() {
  const navigate = useNavigate();
  const { getAction, roleId: currentUserRoleId } = useApi();
  const [searchParams] = useSearchParams();
  const { getPermissionTemplate, getDefaultPermissionTemplate } = usePermissionDataProvider();

  /**
   * Runs when the form is submitted.
   */
  function onSubmit({
    preNominals,
    postNominals,
    firstName,
    lastName,
    email,
    nfqesEmail,
    password,
    phoneNumber,
    roleId,
    organizationId,
    copyProfilesFromUserId,
    globalPermissions,
    inOrganizations,
    inDepartments,
  }: UserFormData) {
    const userCreateAction = getAction('UserCreate');

    return userCreateAction({
      payload: mapUserFormToCreatePayload(
        {
          preNominals,
          postNominals,
          firstName,
          lastName,
          email,
          nfqesEmail,
          password,
          phoneNumber,
          roleId,
          organizationId,
          copyProfilesFromUserId,
          globalPermissions,
          inOrganizations,
          inDepartments,
          confirmPassword: '',
        },
        getPermissionTemplate,
        getDefaultPermissionTemplate,
        {
          includeGlobalPermissions: currentUserRoleId === ROLE_ADMIN_ID,
        }
      ),
    })
      .then((response) => {
        notifications.show({
          title: 'Používateľ bol vytvorený',
          message: `Používateľ ${firstName} ${lastName} bol úspešne vytvorený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (USER_REDIRECT_AFTER_SAVE === 'same') {
          navigate(EDIT_USER_PAGE_PATH.insert({ userId: response.userId }));
        } else if (USER_REDIRECT_AFTER_SAVE === 'list') {
          navigate(USERS_PAGE_PATH.original);
        }
      })
      .catch((error) => {
        if (error.response.data.error.code === DUPLICATE_USER_ERROR_CODE) {
          notifications.show({
            title: 'Chyba!',
            message: 'Používateľ s danou emailovou adresou už existuje.',
            color: ERROR_NOTIFICATION_COLOR,
          });
        } else {
          panic(error);
        }
      });
  }

  return (
    <DashboardLayout
      title="Pridanie nového používateľa"
      breadcrumbs={[
        { title: 'Používatelia', link: USERS_PAGE_PATH.original },
        { title: 'Pridanie nového používateľa' },
      ]}
    >
      <UserForm
        onSubmit={onSubmit}
        initialValues={{
          ...userFormInitialValues,
          organizationId: searchParams.get('organizationId') ?? '',
        }}
        hideSections={{
          password: false,
          inOrganizations: true,
          inDepartments: true,
        }}
        primaryButtonText="Vytvoriť"
      />
    </DashboardLayout>
  );
}
